import {
  fontSizes,
  fontWeights,
  lineHeights,
  letterSpacings,
} from "../primitives/typography";

/** Inter is the text face; Archivo distinguishes money and display figures. */
export type Typeface = "inter" | "archivo";

export type FontWeightValue = (typeof fontWeights)[keyof typeof fontWeights];

export type TextTransform = "uppercase" | "lowercase" | "capitalize" | "none";

export interface TypographyMetrics {
  typeface: Typeface;
  fontSize: number;
  fontWeight: FontWeightValue;
  lineHeight: number;
  letterSpacing: number;
  textTransform?: TextTransform;
}

const scale = {
  /**
   * Heading styles - for screen hierarchy
   * From page titles down to widget labels
   */
  heading: {
    xl: {
      typeface: "inter",
      fontSize: fontSizes["36"],
      fontWeight: fontWeights.bold,
      lineHeight: lineHeights["44"],
      letterSpacing: letterSpacings.tight3,
    },
    lg: {
      typeface: "inter",
      fontSize: fontSizes["28"],
      fontWeight: fontWeights.semiBold,
      lineHeight: lineHeights["36"],
      letterSpacing: letterSpacings.none,
    },
    sm: {
      typeface: "inter",
      fontSize: fontSizes["20"],
      fontWeight: fontWeights.semiBold,
      lineHeight: lineHeights["28"],
      letterSpacing: letterSpacings.none,
    },

    /** 24px - Large section headings, article h2 */
    md: {
      typeface: "inter",
      fontSize: fontSizes["24"],
      fontWeight: fontWeights.semiBold,
      lineHeight: lineHeights["32"],
      letterSpacing: letterSpacings.none,
    },

    /** 18px - Card titles, section headers */
    xs: {
      typeface: "inter",
      fontSize: fontSizes["18"],
      fontWeight: fontWeights.semiBold,
      lineHeight: lineHeights["24"],
      letterSpacing: letterSpacings.none,
    },

    /** 16px - Widget titles, tertiary headings */
    "2xs": {
      typeface: "inter",
      fontSize: fontSizes["16"],
      fontWeight: fontWeights.semiBold,
      lineHeight: lineHeights["20"],
      letterSpacing: letterSpacings.none,
    },
  } satisfies Record<string, TypographyMetrics>,

  /**
   * Body styles - for all readable content
   */
  body: {
    lg: {
      typeface: "inter",
      fontSize: fontSizes["18"],
      fontWeight: fontWeights.regular,
      lineHeight: lineHeights["28"],
      letterSpacing: letterSpacings.none,
    },
    xl: {
      typeface: "inter",
      fontSize: fontSizes["20"],
      fontWeight: fontWeights.regular,
      lineHeight: lineHeights["32"],
      letterSpacing: letterSpacings.none,
    },

    /** 16px - Default body text, standard content */
    md: {
      typeface: "inter",
      fontSize: fontSizes["16"],
      fontWeight: fontWeights.regular,
      lineHeight: lineHeights["24"],
      letterSpacing: letterSpacings.none,
    },

    /** 14px - Secondary content, footnotes, errors */
    sm: {
      typeface: "inter",
      fontSize: fontSizes["14"],
      fontWeight: fontWeights.regular,
      lineHeight: lineHeights["20"],
      letterSpacing: letterSpacings.none,
    },
  } satisfies Record<string, TypographyMetrics>,

  /**
   * Label styles - for tappable elements and field labels
   */
  label: {
    "md-loose": {
      typeface: "inter",
      fontSize: fontSizes["16"],
      fontWeight: fontWeights.medium,
      lineHeight: lineHeights["24"],
      letterSpacing: letterSpacings.none,
    },
    /** 16px - Primary CTAs, prominent buttons */
    md: {
      typeface: "inter",
      fontSize: fontSizes["16"],
      fontWeight: fontWeights.medium,
      lineHeight: lineHeights["20"],
      letterSpacing: letterSpacings.none,
    },

    /** 14px - Standard buttons, tabs, input labels */
    sm: {
      typeface: "inter",
      fontSize: fontSizes["14"],
      fontWeight: fontWeights.medium,
      lineHeight: lineHeights["20"],
      letterSpacing: letterSpacings.wide2,
    },

    /** 14px - Selected options and compact emphasized actions */
    "sm-semibold": {
      typeface: "inter",
      fontSize: fontSizes["14"],
      fontWeight: fontWeights.semiBold,
      lineHeight: lineHeights["20"],
      letterSpacing: letterSpacings.wide2,
    },

    /** 14px - Field labels with relaxed vertical rhythm */
    "sm-loose": {
      typeface: "inter",
      fontSize: fontSizes["14"],
      fontWeight: fontWeights.medium,
      lineHeight: lineHeights["24"],
      letterSpacing: letterSpacings.wide2,
    },
  } satisfies Record<string, TypographyMetrics>,

  /**
   * Number styles - for financial and numerical data
   * Uses Archivo to distinguish money from text
   */
  number: {
    lg: {
      typeface: "archivo",
      fontSize: fontSizes["24"],
      fontWeight: fontWeights.bold,
      lineHeight: lineHeights["32"],
      letterSpacing: letterSpacings.tight3,
    },
    "md-semibold": {
      typeface: "archivo",
      fontSize: fontSizes["16"],
      fontWeight: fontWeights.semiBold,
      lineHeight: lineHeights["24"],
      letterSpacing: letterSpacings.none,
    },
    /** 14px bold - Compact figures in legends and dense rows */
    sm: {
      typeface: "archivo",
      fontSize: fontSizes["14"],
      fontWeight: fontWeights.bold,
      lineHeight: lineHeights["20"],
      letterSpacing: letterSpacings.none,
    },

    /** 36px - Hero portfolio value. One per screen. */
    xl: {
      typeface: "archivo",
      fontSize: fontSizes["36"],
      fontWeight: fontWeights.bold,
      lineHeight: lineHeights["40"],
      letterSpacing: letterSpacings.tight3,
    },

    /** 16px bold - Card amounts, emphasized figures */
    md: {
      typeface: "archivo",
      fontSize: fontSizes["16"],
      fontWeight: fontWeights.bold,
      lineHeight: lineHeights["24"],
      letterSpacing: letterSpacings.none,
    },
  } satisfies Record<string, TypographyMetrics>,

  /**
   * Caption styles - for timestamps, disclaimers, section dividers
   */
  caption: {
    /** 12px - Timestamps, disclaimers, supplementary info */
    sm: {
      typeface: "inter",
      fontSize: fontSizes["12"],
      fontWeight: fontWeights.regular,
      lineHeight: lineHeights["16"],
      letterSpacing: letterSpacings.none,
    },

    /** 12px - Compact UI, filters, badges */
    "sm-medium": {
      typeface: "inter",
      fontSize: fontSizes["12"],
      fontWeight: fontWeights.medium,
      lineHeight: lineHeights["16"],
      letterSpacing: letterSpacings.none,
    },

    /** 12px uppercase - The section label above a group */
    overline: {
      typeface: "inter",
      fontSize: fontSizes["12"],
      fontWeight: fontWeights.semiBold,
      lineHeight: lineHeights["16"],
      letterSpacing: letterSpacings.wide4,
      textTransform: "uppercase",
    },
  } satisfies Record<string, TypographyMetrics>,
};

export const typography = {
  ...scale,
  display: {
    typeface: "inter",
    fontSize: 56,
    fontWeight: "700",
    lineHeight: 64,
    letterSpacing: -0.5,
  },
} as const satisfies Record<string, unknown>;

export type TypographyScale = typeof scale;
export type TypographyCategory = keyof TypographyScale;
export type TypographyVariant<T extends TypographyCategory> =
  keyof TypographyScale[T];
export type TypographyVariantPath =
  | "display"
  | {
      [C in TypographyCategory & string]: `${C}.${TypographyVariant<C> &
        string}`;
    }[TypographyCategory & string];
